import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import {
    Alert,
    Badge,
    Button,
    Collapse,
    DatePicker,
    Modal,
    Radio,
    Result,
    Space,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import moment from 'moment';

import {
    IUtmConstantField,
    IUtmDiagnostics,
    IUtmParseTask,
    IUtmReportSample,
    TASK_STATUS,
    pluralizeValues,
} from './types';
import { useParseTask } from './useParseTask';
import { formatNumberWithSpaces } from '../../../../../helpers/utils/functions';
import { getApiErrorMessage, showSuccessMessage } from '../../../../../helpers/utils/ui';
import { utmSchemesApi } from '../../../../../shared/common/api/endpoints/utmSchemesApi';
import { useTypedSelector } from '../../../../../shared/common/hooks/useTypedSelector';

interface IDiagnosticsModalProps {
    visible: boolean;
    onClose: () => void;
    /** Открыть настройку схемы кабинета: единственный кабинет Яндекс.Директа, если он один */
    onSetupScheme?: () => void;
    /** Кабинет для кнопки «Настроить схему» известен (иначе уводим к таблице) */
    canSetupScheme?: boolean;
}

/** Значение метки бывает длинным — показываем начало, полное открывается по клику и в подсказке */
const VALUE_LIMIT = 80;

const DATE_FORMAT_API = 'YYYY-MM-DD';
const DATE_FORMAT_UI = 'DD.MM.YYYY';

/**
 * Сколько строк отдаёт бэкенд: списки подстановок, форматов и полей — до 15 строк,
 * образцы значений — до 20. Счётчики в шапке и бейджах считают всё, поэтому под
 * таблицами честно пишем, что список обрезан.
 */
const ROWS_LIMIT = 15;
const SAMPLES_LIMIT = 20;

/** Подпись под таблицей, когда бэкенд отдал ровно свой максимум строк */
const limitNote = (shown: number, limit: number) => (
    shown >= limit
        ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Показаны первые {limit} — список ограничен
            </Typography.Text>
        )
        : null
);

/** Пресеты периода по дате регистрации. `all` — запрос без границ, вся история школы */
const PERIOD_PRESET = {
    month: '30',
    quarter: '90',
    year: '365',
    all: 'all',
    /** Произвольный диапазон из RangePicker — кнопкой не выбирается */
    custom: 'custom',
} as const;

type TPeriodPreset = typeof PERIOD_PRESET[keyof typeof PERIOD_PRESET];

const PERIOD_OPTIONS = [
    { label: '30 дней', value: PERIOD_PRESET.month },
    { label: '90 дней', value: PERIOD_PRESET.quarter },
    { label: 'Год', value: PERIOD_PRESET.year },
    { label: 'Вся история', value: PERIOD_PRESET.all },
];

/** Ключи панелей аккордеона — по ним же выбирается раскрытая при открытии окна */
const PANEL = {
    broken: 'broken',
    noScheme: 'no-scheme',
    constant: 'constant',
} as const;

interface IPeriod {
    from: string;
    to: string;
}

/** Дата из ответа бэкенда в привычном виде; null и пустая строка — прочерк */
const formatDate = (value?: string | null): string =>
    (value ? moment(value).format(DATE_FORMAT_UI) : '—');

const ValueCell: FC<{ value: string }> = ({ value }) => {
    const [ expanded, setExpanded ] = useState(false);
    const text = value ?? '';

    if (text.length <= VALUE_LIMIT) {
        return <span style={{ wordBreak: 'break-all' }}>{text}</span>;
    }

    return (
        <Tooltip title={text} placement="topLeft">
            <span
                style={{
                    cursor: 'pointer',
                    display: 'block',
                    wordBreak: expanded ? 'break-all' : 'normal',
                    whiteSpace: expanded ? 'normal' : 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
                onClick={() => setExpanded((current) => !current)}
            >
                {expanded ? text : `${text.slice(0, VALUE_LIMIT)}…`}
            </span>
        </Tooltip>
    );
};

/** Заголовок панели аккордеона: название, подсказка и счётчик проблем */
const panelHeader = (title: string, hint: string, count: number, color: string) => (
    <Space size={8}>
        <span>{title}</span>
        <Tooltip title={hint}>
            <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
        </Tooltip>
        <Badge
            count={count}
            showZero
            overflowCount={99999}
            style={{ backgroundColor: count > 0 ? color : '#bfbfbf' }}
        />
    </Space>
);

/** Таблица образцов значений — общая для блоков «подстановки» и «упаковка без схемы» */
const samplesTable = (samples: IUtmReportSample[]) => (
    <>
    <Table
        rowKey="utm_filter_id"
        size="small"
        dataSource={samples}
        pagination={false}
        scroll={{ y: 260 }}
        columns={[
            { title: 'Метка', dataIndex: 'utm_name', key: 'utm_name', width: 150 },
            {
                title: 'Значение',
                dataIndex: 'value',
                key: 'value',
                render: (value: string) => <ValueCell value={value} />,
            },
            {
                title: 'Регистраций',
                dataIndex: 'registrations',
                key: 'registrations',
                width: 110,
                render: (count: number) => formatNumberWithSpaces(count ?? 0),
            },
            {
                title: 'Последняя регистрация',
                dataIndex: 'last_registration_at',
                key: 'last_registration_at',
                width: 170,
                render: (date: string | null) => formatDate(date),
            },
        ]}
    />
    {limitNote(samples.length, SAMPLES_LIMIT)}
    </>
);

/**
 * Диагностика меток школы: где ссылки размечены неправильно.
 *
 * Показывает, какие подстановки не сработали (в шаблоне забыли фигурные скобки),
 * какие форматы упаковки остались без схемы и в каких полях по всем кликам одно
 * и то же значение. Отсюда же запускается переразбор всех меток школы.
 *
 * Отбор идёт по периоду регистрации: словарь меток хранит всю историю школы, поэтому
 * без периода давно починенные значения висели бы в окне вечно.
 */
const DiagnosticsModal: FC<IDiagnosticsModalProps> = ({
    visible,
    onClose,
    onSetupScheme,
    canSetupScheme = false,
}) => {
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    const schoolId = selectedSchool?.id;

    const [ loading, setLoading ] = useState(false);
    const [ acting, setActing ] = useState(false);
    const [ error, setError ] = useState('');
    const [ data, setData ] = useState<IUtmDiagnostics | null>(null);
    const [ schoolTaskPending, setSchoolTaskPending ] = useState(false);
    const [ preset, setPreset ] = useState<TPeriodPreset>(PERIOD_PRESET.month);
    const [ customRange, setCustomRange ] = useState<IPeriod | null>(null);
    const [ activeKeys, setActiveKeys ] = useState<string[]>([]);

    /** Границы запроса: null — вся история, параметры не передаются */
    const period = useMemo<IPeriod | null>(() => {
        if (preset === PERIOD_PRESET.all) return null;
        if (preset === PERIOD_PRESET.custom) return customRange;

        const days = Number(preset);

        return {
            from: moment().subtract(days - 1, 'days').format(DATE_FORMAT_API),
            to: moment().format(DATE_FORMAT_API),
        };
    }, [ preset, customRange ]);

    const loadReport = useCallback(async () => {
        if (!schoolId) return;

        setLoading(true);
        setError('');
        try {
            const response = await utmSchemesApi.getReport(schoolId, period?.from, period?.to);
            setData((response.data?.data ?? null) as IUtmDiagnostics | null);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось построить диагностику, попробуйте позже'));
        } finally {
            setLoading(false);
        }
    }, [ schoolId, period ]);

    /** Есть ли у школы незавершённая задача — от неё зависит доступность переразбора */
    const loadTaskState = useCallback(async () => {
        if (!schoolId) return null;

        try {
            // integration_id не важен: нужен только признак очереди по школе
            const response = await utmSchemesApi.getList(schoolId, 0);
            const payload = response.data?.data ?? {};
            setSchoolTaskPending(Boolean(payload.school_task_pending));
        } catch {
            // Признак необязателен: бэкенд всё равно ответит 409, если задача идёт
            setSchoolTaskPending(false);
        }
        return null;
    }, [ schoolId ]);

    // Диагностику после переразбора нужно перечитать: цифры меняются.
    // Период сохраняем — loadReport всегда берёт текущий выбор пользователя
    const handleTaskFinished = useCallback(() => {
        setSchoolTaskPending(false);
        loadReport();
    }, [ loadReport ]);

    const { task, error: taskError, timedOut, isRunning, watch, reset } = useParseTask(
        schoolId,
        handleTaskFinished
    );

    // Период не сохраняется между открытиями: каждое открытие — снова 30 дней
    useEffect(() => {
        if (!visible) {
            reset();
            setData(null);
            setPreset(PERIOD_PRESET.month);
            setCustomRange(null);
            return;
        }
        loadTaskState();
    }, [ visible, loadTaskState, reset ]);

    // Отдельно от статуса задачи: смена периода перечитывает только отчёт
    useEffect(() => {
        if (!visible) return;
        loadReport();
    }, [ visible, loadReport ]);

    const summary = data?.summary;
    const brokenTokens = data?.broken?.tokens ?? [];
    const brokenSamples = data?.broken?.samples ?? [];
    const noSchemeShapes = data?.no_scheme?.shapes ?? [];
    const noSchemeSamples = data?.no_scheme?.samples ?? [];
    const constantFields = data?.constant_fields ?? [];
    const hasSuspicious = constantFields.some((field) => field.suspicious);
    const hasPanels = brokenTokens.length > 0 || noSchemeShapes.length > 0 || constantFields.length > 0;
    const allClear =
        Boolean(summary)
        && (summary?.broken_values ?? 0) === 0
        && (summary?.no_scheme_values ?? 0) === 0
        && !hasSuspicious;

    // Раскрываем первую панель с проблемами, остальные оставляем свёрнутыми
    useEffect(() => {
        if (!data) {
            setActiveKeys([]);
            return;
        }

        if (brokenTokens.length > 0) {
            setActiveKeys([ PANEL.broken ]);
        } else if (noSchemeShapes.length > 0) {
            setActiveKeys([ PANEL.noScheme ]);
        } else if (hasSuspicious) {
            setActiveKeys([ PANEL.constant ]);
        } else {
            setActiveKeys([]);
        }
        // Пересчитываем только при новом ответе бэкенда: производные списки
        // берутся из того же `data`, отдельными зависимостями их не указываем
    }, [ data ]);

    const confirmReparse = () => {
        if (!schoolId || !data) return;

        // Переразбор идёт по всему словарю школы, поэтому в подтверждении — число за всю историю
        const packed = summary?.packed_values_total ?? summary?.packed_values ?? 0;
        Modal.confirm({
            title: 'Переразобрать метки школы?',
            content: `Будут заново разобраны все упакованные метки школы — ${formatNumberWithSpaces(packed)} ${pluralizeValues(packed)}. `
                + 'Отчёты по параметрам будут неполными до завершения, обычно это несколько минут.',
            okText: 'Переразобрать',
            cancelText: 'Отмена',
            width: 520,
            onOk: async () => {
                setActing(true);
                setError('');
                try {
                    const response = await utmSchemesApi.reparseSchool(schoolId);
                    showSuccessMessage('Переразбор поставлен в очередь');
                    setSchoolTaskPending(true);
                    watch((response.data?.data ?? null) as IUtmParseTask | null);
                } catch (err) {
                    setError(getApiErrorMessage(err, 'Не удалось поставить переразбор'));
                } finally {
                    setActing(false);
                }
            },
        });
    };

    const handlePresetChange = (value: TPeriodPreset) => {
        setPreset(value);
        setCustomRange(null);
    };

    const handleRangeChange = (dates: [ moment.Moment | null, moment.Moment | null ] | null) => {
        const from = dates?.[0];
        const to = dates?.[1];

        if (!from || !to) {
            // Очистка диапазона возвращает период по умолчанию
            handlePresetChange(PERIOD_PRESET.month);
            return;
        }

        setCustomRange({ from: from.format(DATE_FORMAT_API), to: to.format(DATE_FORMAT_API) });
        setPreset(PERIOD_PRESET.custom);
    };

    const rangeValue: [ moment.Moment, moment.Moment ] | null = customRange
        ? [ moment(customRange.from), moment(customRange.to) ]
        : null;

    return (
        <Modal
            title="Диагностика меток"
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={960}
            style={{ top: 40 }}
            bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}
            destroyOnClose
        >
            <Spin spinning={loading} tip="Строим диагностику, это занимает несколько секунд">
                <div
                    style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        background: '#fff',
                        borderBottom: '1px solid #f0f0f0',
                        padding: '8px 0 12px',
                        marginTop: -8,
                        marginBottom: 12,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            gap: 24,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            marginBottom: 12,
                        }}
                    >
                        <Statistic
                            title={period ? 'Упакованных за период' : 'Упакованных значений'}
                            value={summary?.packed_values ?? 0}
                            groupSeparator=" "
                        />
                        <Statistic title="Разобрано" value={summary?.parsed_values ?? 0} groupSeparator=" " />
                        <Statistic
                            title="Без схемы"
                            value={summary?.no_scheme_values ?? 0}
                            groupSeparator=" "
                            valueStyle={(summary?.no_scheme_values ?? 0) > 0 ? { color: '#ad6800' } : undefined}
                        />
                        <Statistic
                            title="Битых"
                            value={summary?.broken_values ?? 0}
                            groupSeparator=" "
                            valueStyle={(summary?.broken_values ?? 0) > 0 ? { color: '#cf1322' } : undefined}
                        />

                        <Space size={8} style={{ marginLeft: 'auto' }} wrap>
                            <Radio.Group
                                size="small"
                                optionType="button"
                                buttonStyle="solid"
                                options={PERIOD_OPTIONS}
                                value={preset === PERIOD_PRESET.custom ? '' : preset}
                                onChange={(event) => handlePresetChange(event.target.value)}
                            />
                            <DatePicker.RangePicker
                                size="small"
                                format={DATE_FORMAT_UI}
                                placeholder={[ 'Дата от', 'Дата до' ]}
                                style={{ width: 230 }}
                                value={rangeValue}
                                disabledDate={(current) => Boolean(current && current > moment().endOf('day'))}
                                onChange={handleRangeChange}
                            />
                            <Button
                                onClick={confirmReparse}
                                loading={acting}
                                disabled={acting || isRunning || schoolTaskPending || !data}
                            >
                                Переразобрать метки школы
                            </Button>
                        </Space>
                    </div>

                    <Typography.Text type="secondary">
                        {period
                            ? `Показаны значения, с которыми были регистрации с ${formatDate(period.from)} по ${formatDate(period.to)}`
                            : 'Показана вся история'}
                    </Typography.Text>

                    {error && (
                        <Alert type="error" message={error} style={{ marginTop: 12 }} showIcon />
                    )}

                    {taskError && (
                        <Alert
                            type="warning"
                            message={`Не удаётся получить статус переразбора: ${taskError}`}
                            description="Задача продолжает выполняться. Откройте окно позже, чтобы увидеть результат."
                            style={{ marginTop: 12 }}
                            showIcon
                        />
                    )}

                    {task && (
                        <Alert
                            type={task.status === TASK_STATUS.failed ? 'error' : 'info'}
                            message={
                                task.status === TASK_STATUS.done
                                    ? `Переразбор завершён: ${formatNumberWithSpaces(task.result?.inserted ?? 0)} ${pluralizeValues(task.result?.inserted ?? 0)}`
                                    : task.status === TASK_STATUS.failed
                                        ? `Переразбор не удался: ${task.result?.error ?? 'неизвестная ошибка'}`
                                        : task.status === TASK_STATUS.running
                                            ? 'Идёт переразбор меток школы'
                                            : 'Переразбор в очереди'
                            }
                            description={
                                timedOut
                                    ? 'Задача идёт дольше обычного. Откройте окно позже — результат сохранится.'
                                    : undefined
                            }
                            style={{ marginTop: 12 }}
                            showIcon
                        />
                    )}
                </div>

                {allClear && (
                    <Result
                        status="success"
                        title={period ? 'За период проблем нет' : 'Метки настроены корректно'}
                        subTitle={
                            period
                                ? 'Все упакованные метки разобраны, подозрительных полей нет. Проверить всю историю можно переключателем периода.'
                                : 'Все упакованные метки разобраны, подозрительных полей нет.'
                        }
                    />
                )}

                {hasPanels && (
                <Collapse
                    activeKey={activeKeys}
                    onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [ keys ])}
                    expandIconPosition="end"
                >
                    {brokenTokens.length > 0 && (
                        <Collapse.Panel
                            key={PANEL.broken}
                            header={panelHeader(
                                'Не сработали подстановки',
                                'В шаблоне ссылки подстановка должна быть в фигурных скобках: {campaign_id}. '
                                    + 'По этим кликам параметр потерян — проверьте шаблоны кампаний.',
                                summary?.broken_values ?? 0,
                                '#cf1322'
                            )}
                        >
                            <Table
                                rowKey="token"
                                size="small"
                                dataSource={brokenTokens}
                                pagination={false}
                                scroll={{ y: 220 }}
                                columns={[
                                    { title: 'Подстановка', dataIndex: 'token', key: 'token' },
                                    {
                                        title: 'Значений',
                                        dataIndex: 'values',
                                        key: 'values',
                                        width: 140,
                                        render: (count: number) => formatNumberWithSpaces(count ?? 0),
                                    },
                                ]}
                            />
                            {limitNote(brokenTokens.length, ROWS_LIMIT)}
                            {brokenSamples.length > 0 && (
                                <Collapse ghost>
                                    <Collapse.Panel header="Примеры значений" key="broken-samples">
                                        {samplesTable(brokenSamples)}
                                    </Collapse.Panel>
                                </Collapse>
                            )}
                        </Collapse.Panel>
                    )}

                    {noSchemeShapes.length > 0 && (
                        <Collapse.Panel
                            key={PANEL.noScheme}
                            header={panelHeader(
                                'Упаковка без схемы',
                                'Эти метки упакованы, но ни одна схема их не разбирает — параметры из них в отчёты не попадают.',
                                summary?.no_scheme_values ?? 0,
                                '#ad6800'
                            )}
                        >
                            <Table
                                rowKey="shape"
                                size="small"
                                dataSource={noSchemeShapes}
                                pagination={false}
                                scroll={{ y: 220 }}
                                columns={[
                                    { title: 'Формат', dataIndex: 'shape', key: 'shape' },
                                    {
                                        title: 'Значений',
                                        dataIndex: 'values',
                                        key: 'values',
                                        width: 120,
                                        render: (count: number) => formatNumberWithSpaces(count ?? 0),
                                    },
                                    {
                                        title: 'Пример',
                                        dataIndex: 'sample',
                                        key: 'sample',
                                        render: (sample: string) => <ValueCell value={sample} />,
                                    },
                                ]}
                            />
                            {limitNote(noSchemeShapes.length, ROWS_LIMIT)}
                            {noSchemeSamples.length > 0 && (
                                <Collapse ghost>
                                    <Collapse.Panel header="Примеры значений" key="no-scheme-samples">
                                        {samplesTable(noSchemeSamples)}
                                    </Collapse.Panel>
                                </Collapse>
                            )}
                            <Button
                                type="primary"
                                style={{ marginTop: 12 }}
                                onClick={() => {
                                    onClose();
                                    onSetupScheme?.();
                                }}
                            >
                                {canSetupScheme ? 'Настроить схему' : 'Перейти к кабинетам'}
                            </Button>
                        </Collapse.Panel>
                    )}

                    {constantFields.length > 0 && (
                        <Collapse.Panel
                            key={PANEL.constant}
                            header={panelHeader(
                                'Одинаковые значения в поле',
                                'Похоже, в шаблоне забыли фигурные скобки: во всех кликах записано одно и то же слово. '
                                    + 'Строки без пометки могут быть нормой — например, одна кампания или один тип размещения.',
                                constantFields.length,
                                '#fa8c16'
                            )}
                        >
                            <Table
                                rowKey={(field: IUtmConstantField) => `${field.scheme_id}_${field.utm_name_id}`}
                                size="small"
                                dataSource={constantFields}
                                pagination={false}
                                scroll={{ y: 220 }}
                                columns={[
                                    { title: 'Схема', dataIndex: 'scheme_name', key: 'scheme_name' },
                                    { title: 'Параметр', dataIndex: 'label', key: 'label', width: 180 },
                                    {
                                        title: 'Значение',
                                        dataIndex: 'value',
                                        key: 'value',
                                        width: 200,
                                        render: (value: string, field: IUtmConstantField) => (
                                            <span>
                                                {value}
                                                {field.suspicious && (
                                                    <Tag color="orange" style={{ marginLeft: 8 }}>
                                                        проверьте шаблон
                                                    </Tag>
                                                )}
                                            </span>
                                        ),
                                    },
                                    {
                                        title: 'Строк',
                                        dataIndex: 'values',
                                        key: 'values',
                                        width: 120,
                                        render: (count: number) => formatNumberWithSpaces(count ?? 0),
                                    },
                                ]}
                            />
                            {limitNote(constantFields.length, ROWS_LIMIT)}
                        </Collapse.Panel>
                    )}
                </Collapse>
                )}
            </Spin>
        </Modal>
    );
};

export default DiagnosticsModal;
